import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, Users, MousePointerClick, TrendingUp, Heart, MessageCircle, Share2, Pencil, Trash2, Plus, Loader2, FlaskConical, X } from 'lucide-react';

const STATUSES = ['ACTIVE', 'PAUSED', 'IN_PROCESS', 'WITH_ISSUES', 'ARCHIVED', 'COMPLETED'];
const OBJECTIVES = ['AWARENESS', 'TRAFFIC', 'ENGAGEMENT', 'LEADS', 'SALES', 'APP_PROMOTION', 'OUTCOME_AWARENESS', 'OUTCOME_TRAFFIC', 'OUTCOME_ENGAGEMENT', 'OUTCOME_LEADS', 'OUTCOME_SALES'];

const EMPTY = {
  name: '', platform: 'meta', ad_account_id: '', status: 'PAUSED', objective: 'AWARENESS', buying_type: 'AUCTION',
  created_date: new Date().toISOString().slice(0, 10), budget_type: 'daily', budget: '', spent: '',
  impressions: '', reach: '', clicks: '', ctr: '', likes: '', comments: '', shares: '',
};

const num = (v, int = false) => (v === '' || v === null || v === undefined ? (int ? 0 : 0) : (int ? parseInt(v, 10) || 0 : parseFloat(v) || 0));

const statusStyle = (s) => ({
  ACTIVE: 'bg-green-500/20 text-green-300 border-green-500/40',
  PAUSED: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
}[s] || 'bg-white/10 text-white/70 border-white/20');

// Mirrors the "My Campaigns" card in AdGlobe so admins see exactly what users will see
const CampaignPreview = ({ c, onEdit, onDelete }) => {
  const ctr = c.ctr === '' || c.ctr == null ? (num(c.impressions) ? (num(c.clicks) / num(c.impressions)) * 100 : 0) : num(c.ctr);
  const budget = num(c.budget);
  return (
    <div className="p-4 rounded-xl bg-[#0f172a] border border-white/10 text-white" data-testid={`sim-card-${c.id || 'preview'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-white">{c.name || 'Campaign name'}</h3>
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${statusStyle(c.status)}`}>{c.status}</span>
          </div>
          <p className="text-xs text-white/50 mt-0.5">{c.objective} • Created {c.created_date ? new Date(c.created_date).toLocaleDateString() : '—'}</p>
        </div>
        {onEdit && (
          <div className="flex items-center gap-1">
            <button onClick={onEdit} className="p-1.5 rounded hover:bg-white/10 text-blue-400" data-testid={`sim-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={onDelete} className="p-1.5 rounded hover:bg-white/10 text-red-400" data-testid={`sim-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mb-3 p-2 rounded-lg bg-white/5">
        <div><div className="text-sm font-semibold">₦{budget.toLocaleString()}{c.budget_type === 'daily' && budget ? '/day' : ''}</div><div className="text-xs text-white/50">Budget</div></div>
        <div className="text-right"><div className="text-sm font-semibold">₦{num(c.spent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div><div className="text-xs text-white/50">Spent</div></div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {[[Eye, 'Impressions', num(c.impressions, true).toLocaleString()], [Users, 'Reach', num(c.reach, true).toLocaleString()], [MousePointerClick, 'Clicks', num(c.clicks, true).toLocaleString()], [TrendingUp, 'CTR', `${ctr.toFixed(2)}%`]].map(([Icon, label, val]) => (
          <div key={label} className="p-2 rounded-lg bg-white/5">
            <div className="flex items-center gap-1 text-xs text-white/50 mb-1"><Icon className="h-3 w-3" /> {label}</div>
            <div className={`font-semibold text-sm ${label === 'CTR' ? 'text-cyan-400' : ''}`}>{val}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="p-2 rounded-lg bg-pink-500/10 border border-pink-500/30"><div className="flex items-center gap-1 text-xs text-pink-300 mb-1"><Heart className="h-3 w-3" /> Likes</div><div className="font-semibold text-pink-400 text-sm">{num(c.likes, true).toLocaleString()}</div></div>
        <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/30"><div className="flex items-center gap-1 text-xs text-blue-300 mb-1"><MessageCircle className="h-3 w-3" /> Comments</div><div className="font-semibold text-blue-400 text-sm">{num(c.comments, true).toLocaleString()}</div></div>
        <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/30"><div className="flex items-center gap-1 text-xs text-green-300 mb-1"><Share2 className="h-3 w-3" /> Shares</div><div className="font-semibold text-green-400 text-sm">{num(c.shares, true).toLocaleString()}</div></div>
      </div>
      <p className="text-[11px] text-white/40 mt-2">{c.platform === 'snapchat' ? 'Snapchat' : 'Meta (Facebook / Instagram)'}{c.ad_account_id ? ` • Ad account ${c.ad_account_id}` : ' • All ad accounts'}</p>
    </div>
  );
};

const Field = ({ label, id, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    {children}
  </div>
);

export const AdSimulateTab = () => {
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [count, setCount] = useState(100);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/ad-simulations');
      setSims(data.simulations || []);
    } catch (e) {
      toast.error('Failed to load simulations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) return toast.error('Campaign name is required');
    setSaving(true);
    if (!editingId) {
      const n = parseInt(count, 10);
      if (!n || n < 1 || n > 500) { setSaving(false); return toast.error('Number of campaigns must be between 1 and 500'); }
      try {
        const { data } = await api.post('/admin/ad-simulations/bulk-random', {
          base_name: form.name.trim(), platform: form.platform, ad_account_id: form.ad_account_id || null, status: form.status, count: n,
        });
        toast.success(`${data.count} simulated campaigns created`);
        reset();
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create simulations');
      } finally {
        setSaving(false);
      }
      return;
    }
    const payload = {
      ...form, ad_account_id: form.ad_account_id || null,
      budget: num(form.budget), spent: num(form.spent), impressions: num(form.impressions, true), reach: num(form.reach, true),
      clicks: num(form.clicks, true), ctr: form.ctr === '' ? null : num(form.ctr), cpm: null,
      likes: num(form.likes, true), comments: num(form.comments, true), shares: num(form.shares, true),
    };
    try {
      if (editingId) await api.put(`/admin/ad-simulations/${editingId}`, payload);
      else await api.post('/admin/ad-simulations', payload);
      toast.success(editingId ? 'Simulation updated' : 'Simulated campaign created');
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save simulation');
    } finally {
      setSaving(false);
    }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(s).map(([k, v]) => [k, v === null ? '' : v])) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete simulated campaign "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/ad-simulations/${s.id}`);
      toast.success('Simulation deleted');
      setSims((prev) => prev.filter((x) => x.id !== s.id));
      if (editingId === s.id) reset();
    } catch {
      toast.error('Failed to delete simulation');
    }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete ALL ${sims.length} simulated campaigns? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete('/admin/ad-simulations');
      toast.success(`${data.deleted} simulations deleted`);
      setSims([]);
      reset();
    } catch {
      toast.error('Failed to delete simulations');
    }
  };

  const numInput = (k, label, step = '1') => (
    <Field label={label} id={`sim-${k}`}>
      <Input id={`sim-${k}`} type="number" min="0" step={step} value={form[k]} onChange={set(k)} placeholder="0" data-testid={`sim-${k}-input`} />
    </Field>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <Card className="border-2 lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-orange-500" /> {editingId ? 'Edit Simulated Campaign' : 'Create Simulated Campaign'}</span>
            {editingId && <Button variant="ghost" size="sm" onClick={reset} data-testid="sim-cancel-edit"><X className="h-4 w-4 mr-1" /> Cancel</Button>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="sim-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={editingId ? 'Campaign name *' : 'Base name * (e.g. "APC network" → APC network (1 fb), APC network (2 ig) …)'} id="sim-name"><Input id="sim-name" value={form.name} onChange={set('name')} placeholder="APC network" data-testid="sim-name-input" /></Field>
              <Field label="Platform" id="sim-platform">
                <Select value={form.platform} onValueChange={set('platform')}>
                  <SelectTrigger id="sim-platform" data-testid="sim-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="meta">Meta (Facebook / Instagram list)</SelectItem><SelectItem value="snapchat">Snapchat</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Status" id="sim-status">
                <Select value={form.status} onValueChange={set('status')}>
                  <SelectTrigger id="sim-status" data-testid="sim-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {editingId && <Field label="Objective" id="sim-objective">
                <Select value={form.objective} onValueChange={set('objective')}>
                  <SelectTrigger id="sim-objective" data-testid="sim-objective-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>}
              {editingId && <Field label="Created date" id="sim-created"><Input id="sim-created" type="date" value={form.created_date} onChange={set('created_date')} data-testid="sim-created-input" /></Field>}
              {!editingId && (
                <Field label="Number of campaigns to create (1–500)" id="sim-count">
                  <Input id="sim-count" type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} data-testid="sim-count-input" />
                </Field>
              )}
              <Field label="Ad account ID (optional — blank = everyone)" id="sim-account"><Input id="sim-account" value={form.ad_account_id} onChange={set('ad_account_id')} placeholder="act_123456789" data-testid="sim-account-input" /></Field>
              {editingId && <Field label="Buying type" id="sim-buying">
                <Select value={form.buying_type} onValueChange={set('buying_type')}>
                  <SelectTrigger id="sim-buying" data-testid="sim-buying-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AUCTION">Interest</SelectItem><SelectItem value="RESERVED">Domination</SelectItem></SelectContent>
                </Select>
              </Field>}
              {editingId && <Field label="Budget type" id="sim-budget-type">
                <Select value={form.budget_type} onValueChange={set('budget_type')}>
                  <SelectTrigger id="sim-budget-type" data-testid="sim-budget-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="lifetime">Lifetime</SelectItem></SelectContent>
                </Select>
              </Field>}
              {editingId && numInput('budget', 'Budget (₦)', '0.01')}
              {editingId && numInput('spent', 'Spent (₦)', '0.01')}
              {editingId && numInput('impressions', 'Impressions')}
              {editingId && numInput('reach', 'Reach')}
              {editingId && numInput('clicks', 'Clicks')}
              {editingId && numInput('ctr', 'CTR % (blank = auto from clicks ÷ impressions)', '0.01')}
              {editingId && numInput('likes', 'Likes')}
              {editingId && numInput('comments', 'Comments')}
              {editingId && numInput('shares', 'Shares')}
            </div>
            {editingId ? (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">Live preview</p>
                <CampaignPreview c={form} />
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1" data-testid="sim-random-rules">
                <p className="font-medium text-foreground">Creates {count || 0} campaigns with random data:</p>
                <p>Names: <b>{form.name || 'Base name'} (1 fb)</b>, <b>{form.name || 'Base name'} (2 ig)</b> … • Objective AWARENESS • Budget empty</p>
                <p>Reach 800–4,556 • Impressions 2,699–37,567 • Clicks 129–670 • CTR = clicks ÷ impressions</p>
                <p>Likes 100–1,000 • Comments 11–100 • Shares 23–65 • Spent ₦3,578–₦6,789 • Created 2026‑10‑09 → 2026‑10‑16</p>
              </div>
            )}
            <Button type="submit" disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="sim-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingId ? 'Save changes' : `Create ${count || 0} simulated campaign${Number(count) === 1 ? '' : 's'}`}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-2 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Simulated campaigns ({sims.length})</span>
            {sims.length > 0 && (
              <Button variant="destructive" size="sm" onClick={removeAll} data-testid="sim-delete-all-btn">
                <Trash2 className="h-4 w-4 mr-1" /> Delete all simulations
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-h-[80vh] overflow-y-auto" data-testid="sim-list">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
            : sims.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No simulated campaigns yet. They will appear inside users' "My Campaigns" list in AdGlobe.</p>
            : sims.map((s) => <CampaignPreview key={s.id} c={s} onEdit={() => edit(s)} onDelete={() => remove(s)} />)}
        </CardContent>
      </Card>
    </div>
  );
};
